import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Send, Bot, User, Sparkles, Plus, MessageSquare, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';


const RETRIEVAL_LABELS = {
  'name-match': 'Found via tutor/student name match',
  'keyword': 'Found via keyword match',
  'semantic': 'Found via semantic search',
  'semantic-empty': 'Semantic search ran — no matches',
  'policy': 'Found in policy documents',
  'macro': 'Aggregated across all sessions',
  'tutor-report': 'Tutor report data',
  'issue-examples': 'Found via issue examples',
  'issue-examples-reranked': 'Found via semantic ranking',
  'issue-examples-empty': 'Issue search ran — no matches',
  'structured-filter': 'Found via structured filter',
  'structured-filter-empty': 'Structured filter ran — no matches',
  'none': 'No retrieval — general overview',
};

const RetrievalBadge = ({ retrieval, isDark }) => {
  if (!retrieval?.path) return null;
  const label = RETRIEVAL_LABELS[retrieval.path] || retrieval.path;
  const count = retrieval.match_count;
  const empty = retrieval.path === 'semantic-empty' || retrieval.path === 'none' || retrieval.path === 'issue-examples-empty' || retrieval.path === 'structured-filter-empty';
  return (
    <span className={`inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
      empty
        ? isDark ? 'border-amber-500/20 text-amber-400/70 bg-amber-500/5' : 'border-amber-200 text-amber-700 bg-amber-50'
        : isDark ? 'border-[#27272a] text-[#71717a] bg-[#0f0f0f]' : 'border-[#e5e5e5] text-[#666] bg-[#fafafa]'
    }`}>
      {label}{count > 0 ? ` · ${count} ${count === 1 ? 'lesson' : 'lessons'}` : ''}
    </span>
  );
};

const Message = ({ message, isDark }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''} animate-slide-up`}>
      <div className={`
        w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
        ${isUser
          ? isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'
          : 'accent-gradient'
        }
      `}>
        {isUser ? (
          <User className={`h-4 w-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`} />
        ) : (
          <Bot className="h-4 w-4 text-white" />
        )}
      </div>
      <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className={`
          inline-block p-4 rounded-lg text-sm leading-relaxed
          ${isUser
            ? isDark ? 'bg-[#18181b] text-white' : 'bg-[#f0f0f0] text-[#1a1a1a]'
            : isDark ? 'bg-[#121212] border border-[#27272a] text-[#e4e4e7]' : 'bg-white border border-[#e5e5e5] text-[#333333]'
          }
        `}>
          {isUser ? (
            <div className="whitespace-pre-wrap">{message.content}</div>
          ) : (
            <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-li:my-0.5 prose-headings:my-2 prose-headings:text-sm prose-headings:font-semibold">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          )}
        </div>
        {!isUser && message.retrieval && (
          <div>
            <RetrievalBadge retrieval={message.retrieval} isDark={isDark} />
          </div>
        )}
        {message.data_sources?.length > 0 && (
          <div className={`mt-1.5 text-[11px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            Based on: {message.data_sources.join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
};

const Assistant = () => {
  const { organizationId, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedTutors, setSuggestedTutors] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const dragRef = useRef(null);

  // Load chat history
  useEffect(() => {
    if (!organizationId || !user) return;
    const loadHistory = async () => {
      const { data } = await supabase
        .from('intelligence_chats')
        .select('id, title, updated_at')
        .eq('organization_id', organizationId)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(50);
      setChatHistory(data || []);
    };
    loadHistory();
  }, [organizationId, user]);

  // Load top tutor names for dynamic suggestions
  useEffect(() => {
    if (!organizationId) return;
    const loadTutors = async () => {
      const { data } = await supabase
        .from('sessions')
        .select('tutor_name')
        .eq('organization_id', organizationId)
        .order('lesson_timestamp', { ascending: false })
        .limit(50);
      if (data) {
        const counts = {};
        data.forEach(s => { counts[s.tutor_name] = (counts[s.tutor_name] || 0) + 1; });
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([name]) => name);
        setSuggestedTutors(top);
      }
    };
    loadTutors();
  }, [organizationId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]') || scrollRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, loading]);

  // Draggable sidebar
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    dragRef.current = e.clientX;
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const delta = e.clientX - dragRef.current;
      dragRef.current = e.clientX;
      setSidebarWidth(prev => Math.max(200, Math.min(500, prev + delta)));
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const getSuggestedQuestions = () => {
    const questions = [];
    if (suggestedTutors[0]) questions.push(`Generate a performance report for ${suggestedTutors[0]}`);
    if (suggestedTutors[1]) questions.push(`How is ${suggestedTutors[1]} performing?`);
    questions.push(
      'Find lessons where the student seemed bored',
      'Which tutors have the most flagged issues?',
      'What are the common quality concerns this month?',
      'Compare engagement across the top tutors',
    );
    return questions.slice(0, 6);
  };

  // Save chat to DB
  const saveChat = async (chatId, msgs) => {
    const title = msgs.find(m => m.role === 'user')?.content?.slice(0, 60) || 'New chat';
    const cleanMsgs = msgs.map(m => ({ role: m.role, content: m.content, data_sources: m.data_sources, retrieval: m.retrieval }));

    if (chatId) {
      await supabase
        .from('intelligence_chats')
        .update({ messages: cleanMsgs, title, updated_at: new Date().toISOString() })
        .eq('id', chatId);
      setChatHistory(prev => prev.map(c => c.id === chatId ? { ...c, title, updated_at: new Date().toISOString() } : c));
      return chatId;
    } else {
      const { data } = await supabase
        .from('intelligence_chats')
        .insert({ organization_id: organizationId, user_id: user.id, messages: cleanMsgs, title })
        .select('id, title, updated_at')
        .single();
      if (data) {
        setChatHistory(prev => [data, ...prev]);
        return data.id;
      }
      return null;
    }
  };

  const loadChat = async (chatId) => {
    const { data } = await supabase
      .from('intelligence_chats')
      .select('messages')
      .eq('id', chatId)
      .single();
    if (data?.messages) {
      setMessages(data.messages);
      setActiveChatId(chatId);
    }
  };

  const deleteChat = async (chatId, e) => {
    e.stopPropagation();
    await supabase.from('intelligence_chats').delete().eq('id', chatId);
    setChatHistory(prev => prev.filter(c => c.id !== chatId));
    if (activeChatId === chatId) {
      setActiveChatId(null);
      setMessages([]);
    }
  };

  const startNewChat = () => {
    setActiveChatId(null);
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || !organizationId) return;

    const userMsg = { role: 'user', content: msg };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const session = (await supabase.auth.getSession()).data.session;
      const history = newMessages.slice(-6).map(m => ({ role: m.role, content: m.content }));

      const xhr = new XMLHttpRequest();
      const data = await new Promise((resolve, reject) => {
        xhr.open('POST', `${SUPABASE_URL}/functions/v1/intelligence-chat-v2`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${session?.access_token}`);
        xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY);
        xhr.timeout = 120000;
        xhr.onload = () => {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Invalid response')); }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));
        xhr.send(JSON.stringify({
          message: msg,
          organization_id: organizationId,
          conversation_history: history,
        }));
      });

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.response) {
        throw new Error('No response received from the AI');
      }

      const assistantMsg = {
        role: 'assistant',
        content: data.response,
        data_sources: data.data_sources || [],
        retrieval: data.retrieval || null,
      };
      const finalMessages = [...newMessages, assistantMsg];
      setMessages(finalMessages);

      // Save to DB
      const chatId = await saveChat(activeChatId, finalMessages);
      if (chatId && !activeChatId) setActiveChatId(chatId);
    } catch (err) {
      console.error('Intelligence chat error:', err);
      toast.error('Failed to get response: ' + (err.message || 'Unknown error'));
      setMessages(prev => prev.slice(0, -1));
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return 'Today';
    if (diff < 172800000) return 'Yesterday';
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  return (
    <div className={`flex h-[calc(100vh-60px)] -m-6 ${isDragging ? 'select-none' : ''}`}>
      {/* Chat History Sidebar */}
      <div
        className={`flex-shrink-0 flex flex-col border-r ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#f5f3f0] border-[#e5e5e5]'}`}
        style={{ width: sidebarWidth }}
      >
        {/* New Chat Button */}
        <div className="p-3">
          <button
            onClick={startNewChat}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isDark
                ? 'bg-[#18181b] hover:bg-[#27272a] text-white border border-[#27272a]'
                : 'bg-white hover:bg-[#eee] text-[#1a1a1a] border border-[#e0e0e0]'
            }`}
          >
            <Plus className="h-4 w-4" />
            New chat
          </button>
        </div>

        {/* Chat List */}
        <ScrollArea className="flex-1 px-2">
          <div className="space-y-0.5 pb-2">
            {chatHistory.length === 0 ? (
              <div className={`text-center py-8 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                No conversations yet
              </div>
            ) : (
              chatHistory.map(chat => (
                <div
                  key={chat.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => loadChat(chat.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadChat(chat.id); } }}
                  className={`w-full grid grid-cols-[14px_minmax(0,1fr)_28px] items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors cursor-pointer ${
                    activeChatId === chat.id
                      ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e4df] text-[#1a1a1a]'
                      : isDark ? 'hover:bg-[#18181b] text-[#a1a1aa]' : 'hover:bg-[#ebe7e2] text-[#666]'
                  }`}
                >
                  <MessageSquare className="h-3.5 w-3.5 opacity-50" />
                  <div className="min-w-0">
                    <div className="block w-full overflow-hidden text-ellipsis whitespace-nowrap pr-1 text-xs" title={chat.title}>
                      {chat.title}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#aaa]'}`}>
                      {formatDate(chat.updated_at)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this chat?')) deleteChat(chat.id, e);
                    }}
                    aria-label="Delete chat"
                    title="Delete chat"
                    className={`justify-self-end p-1.5 rounded transition-colors ${
                      isDark ? 'bg-[#27272a] hover:bg-red-500/20 text-[#a1a1aa] hover:text-red-400' : 'bg-[#e5e5e5] hover:bg-red-100 text-[#666] hover:text-red-600'
                    }`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Draggable Divider */}
      <div
        onMouseDown={handleMouseDown}
        className={`w-1 flex-shrink-0 cursor-col-resize transition-colors ${
          isDragging
            ? 'bg-[#586f8d]'
            : isDark ? 'hover:bg-[#3f3f46]' : 'hover:bg-[#d0d0d0]'
        }`}
      />

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Messages */}
        <ScrollArea className="flex-1 px-6 py-4" ref={scrollRef}>
          <div className="max-w-3xl mx-auto space-y-6 pb-4">
            {messages.length === 0 ? (
              <div className="py-16">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 accent-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                  <h2 className={`text-xl font-[Manrope] font-medium mb-2 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                    Assistant
                  </h2>
                  <p className={`text-sm max-w-md mx-auto ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>
                    Ask anything across your sessions, tutors, students, issues and policies. Semantic search finds lessons by meaning — try "lessons where the student seemed bored".
                  </p>
                </div>

              </div>
            ) : (
              messages.map((msg, idx) => (
                <Message key={idx} message={msg} isDark={isDark} />
              ))
            )}

            {loading && (
              <div className="flex gap-3 animate-slide-up">
                <div className="w-8 h-8 rounded-lg accent-gradient flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className={`rounded-lg p-4 border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
                  <div className="flex gap-1">
                    <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-[#52525b]' : 'bg-[#888888]'}`} style={{ animationDelay: '0ms' }} />
                    <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-[#52525b]' : 'bg-[#888888]'}`} style={{ animationDelay: '150ms' }} />
                    <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-[#52525b]' : 'bg-[#888888]'}`} style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className={`px-6 py-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          <div className="max-w-3xl mx-auto flex gap-3">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about a lesson, tutor, or trend..."
              className={`flex-1 min-h-[50px] max-h-[150px] resize-none ${isDark ? 'bg-[#121212] border-[#27272a] focus:border-[#3f3f46]' : 'bg-white border-[#e5e5e5] focus:border-[#d0d0d0]'}`}
              disabled={loading}
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className={`h-auto px-6 ${isDark ? 'accent-gradient text-white' : 'bg-[#1a1a1a] hover:bg-[#333333] text-white'}`}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className={`text-xs mt-2 text-center ${isDark ? 'text-[#52525b]' : 'text-[#888888]'}`}>
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default Assistant;
